'use client'

import {
  Cell,
  Checkbox,
  Divider,
  InfoBanner,
  InputSelect,
  InputText,
  Radio,
  Spacer,
} from '@vinted/web-ui'
import { omit } from 'lodash'
import { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { useIntl } from 'react-intl'

import { ErrorItem, useFetch } from '@marketplace-web/core-api/api-client-util'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { List } from '@marketplace-web/common-components/list-ui'
import { isValueInObject } from '@marketplace-web/shared/utils'
import { InputBirthdate } from '@marketplace-web/common-components/input-birthdate-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import {
  CountrySelectionModel,
  transformCountriesSelectionDtos,
  getCountries,
  transformCountryDtos,
} from '@marketplace-web/country/country-data'
import { NationalitySelect } from '@marketplace-web/nationality-select/nationality-select-feature'
import { NationalityCountryModel } from '@marketplace-web/nationality-select/nationality-select-data'
import { WorldCountry } from '@marketplace-web/intellectual-property-infringement/intellectual-property-infringement-data'
import {
  viewScreenEvent,
  BusinessRegistrationAddressModel,
  BusinessRegistrationModel,
  getCountriesSelection,
} from '@marketplace-web/business-accounts/business-accounts-data'
import {
  InputDropdownRenderProps,
  InputDropdown,
} from '@marketplace-web/common-components/input-dropdown-ui'
import { Heading } from '@marketplace-web/business-accounts/business-accounts-ui'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { CountryId, NewRepresentativeField } from '../../../../../constants'
import { GeneralError } from '../../../GeneralError'
import { Section } from '../../../Section'
import { camelCase } from '../../utils'
import { validateName } from '../utils'
import AddressDetails from '../../../../AddressDetails'

type Props = {
  defaultValues: BusinessRegistrationModel
  apiErrors: Array<ErrorItem> | null
  showGeneralError: boolean
}

const TRANSLATION_PREFIX = 'business.registration'

const RepresentativeStep = ({ defaultValues, apiErrors, showGeneralError }: Props) => {
  const { track } = useTracking()
  const breakpoints = useBreakpoint()
  const translate = useTranslate(`${TRANSLATION_PREFIX}.business_representative`)
  const inputBirthdateTranslate = useTranslate()
  const translateBusinessAddress = useTranslate(`${TRANSLATION_PREFIX}.addresses`)
  const inputDropdownTranslate = useTranslate()
  const intl = useIntl()

  const { fetch: fetchCountries, transformedData: countriesForAddresses } = useFetch(
    getCountries,
    response => transformCountryDtos(response.countries),
  )

  const countriesForAddressesOptions =
    countriesForAddresses?.map(country => ({
      value: country.id,
      label: country.titleLocal,
    })) || []

  const {
    register,
    control,
    reset,
    setError,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext()

  const nationality = watch(NewRepresentativeField.Nationality)
  const countryInput = watch(NewRepresentativeField.Country)
  const representativeCountryInput = watch(NewRepresentativeField.RepresentativeCountry)
  const isSameLegalAddress = watch(NewRepresentativeField.IsSameLegalAddress)

  const validationMessage = useFormValidationMessage(errors, TRANSLATION_PREFIX)
  const { businessAddress } = defaultValues.addresses
  const [countries, setCountries] = useState<Array<CountrySelectionModel>>([])

  const formCountryId = Number(defaultValues.addresses.businessAddress.countryId)

  const fullNameInputAbTest = useAbTest('b2c_full_name_input_uk_fr')

  const isFullNameInputAbTestExposed =
    formCountryId === CountryId.UK || formCountryId === CountryId.FR
  const isUpdatedNameInputs = fullNameInputAbTest?.variant === 'on' && isFullNameInputAbTestExposed

  useTrackAbTest(fullNameInputAbTest, isFullNameInputAbTestExposed)

  useEffect(() => {
    async function fetchCountriesSelection() {
      const response = await getCountriesSelection()

      if ('errors' in response) return

      setCountries(transformCountriesSelectionDtos(response.countries))
    }

    fetchCountriesSelection()
    fetchCountries()
  }, [fetchCountries])

  useEffect(() => {
    register(NewRepresentativeField.Nationality, { required: true })
  }, [register])

  useEffect(() => {
    register(NewRepresentativeField.Country, { required: true })
  }, [register])

  useEffect(() => {
    track(
      viewScreenEvent({
        screen: 'registration_business_representative',
      }),
    )
  }, [track])

  useEffect(() => {
    if (!apiErrors) return

    apiErrors.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (!isValueInObject(fieldName, NewRepresentativeField) && fieldName !== 'ubos') return

      setError(fieldName, { type: 'manual', message: value })
    })
  }, [apiErrors, setError])

  useEffect(() => {
    reset({ businessRepresentative: defaultValues.businessRepresentative })
  }, [reset, defaultValues, countries])

  const handleNationalityChange = (onChange: (...event: Array<unknown>) => void) => {
    return (selectedNationalityCountry: NationalityCountryModel | null) => {
      onChange(selectedNationalityCountry)
    }
  }

  const handleCountrySelect =
    (
      selectedCountry: WorldCountry | undefined,
      inputRenderProps: InputDropdownRenderProps,
      isForLegalAddress: boolean,
    ) =>
    () => {
      const handledField = isForLegalAddress
        ? NewRepresentativeField.RepresentativeCountry
        : NewRepresentativeField.Country
      inputRenderProps.setIsSaveEnabled(true)

      setValue(handledField, selectedCountry, {
        shouldValidate: true,
        shouldDirty: true,
      })

      if (breakpoints.phones) return

      inputRenderProps.closeDropdown()
    }

  const renderCountry = (
    countryOption: WorldCountry,
    inputRenderProps: InputDropdownRenderProps,
    isForLegalAddress: boolean,
  ) => {
    const country = isForLegalAddress ? representativeCountryInput : countryInput
    const isCountrySelected = countryOption.code === country?.code
    const labelId = `id-${countryOption.code}`

    return (
      <Cell
        key={countryOption.code}
        title={countryOption.title}
        onClick={handleCountrySelect(countryOption, inputRenderProps, isForLegalAddress)}
        id={labelId}
        suffix={
          <Radio
            name={`radio-${countryOption.code}`}
            checked={isCountrySelected}
            onChange={() => undefined}
            aria={{
              'aria-labelledby': labelId,
            }}
          />
        }
      />
    )
  }

  const renderCountriesList = (inputRenderProps: InputDropdownRenderProps) => (
    <List>{countries.map(country => renderCountry(country, inputRenderProps, false))}</List>
  )

  const renderBusinessAddress = (address: BusinessRegistrationAddressModel) => (
    <div className="c-input c-input--wide">
      <div className="c-input__title">{translate('business_address.title')}</div>
      <div className="c-input__content">
        <div className="u-flexbox c-input__value bottom-border-none">
          <AddressDetails address={address} styling={Cell.Styling.Tight} />
        </div>
      </div>
    </div>
  )

  const renderRepresentativeAddressForm = () => (
    <>
      <InputSelect
        {...register(NewRepresentativeField.RepresentativeCountry, {
          required: true,
        })}
        styling={InputSelect.Styling.Wide}
        title={translateBusinessAddress('legal_representative_address.country_id.title')}
        options={countriesForAddressesOptions}
        required
        validation={renderValidation(
          validationMessage(NewRepresentativeField.RepresentativeCountry),
        )}
      />
      <InputText
        {...register(NewRepresentativeField.RepresentativeAddressLine1, {
          required: true,
        })}
        styling={InputText.Styling.Wide}
        title={translateBusinessAddress('business_address.line1.title')}
        placeholder={translateBusinessAddress('business_address.line1.placeholder')}
        validation={renderValidation(
          validationMessage(NewRepresentativeField.RepresentativeAddressLine1),
        )}
      />
      <InputText
        {...register(NewRepresentativeField.RepresentativeAddressLine2)}
        styling={InputText.Styling.Wide}
        title={translateBusinessAddress('business_address.line2.title')}
        placeholder={translateBusinessAddress('business_address.line2.placeholder')}
        validation={renderValidation(
          validationMessage(NewRepresentativeField.RepresentativeAddressLine2),
        )}
      />
      <InputText
        {...register(NewRepresentativeField.RepresentativePostalCode)}
        styling={InputText.Styling.Wide}
        title={translateBusinessAddress('business_address.postal_code.title')}
        placeholder={translateBusinessAddress('business_address.postal_code.placeholder')}
        validation={renderValidation(
          validationMessage(NewRepresentativeField.RepresentativePostalCode),
        )}
      />
      <InputText
        {...register(NewRepresentativeField.RepresentativeCity)}
        styling={InputText.Styling.Wide}
        title={translateBusinessAddress('business_address.city.title')}
        placeholder={translateBusinessAddress('business_address.city.placeholder')}
        validation={renderValidation(validationMessage(NewRepresentativeField.RepresentativeCity))}
      />
    </>
  )

  return (
    <SeparatedList separator={<Spacer size={Spacer.Size.X2Large} />}>
      <Heading
        styling={Cell.Styling.Tight}
        title={translate('title')}
        subtitle={translate('subtitle')}
      />
      {showGeneralError && <GeneralError />}
      {!isUpdatedNameInputs && (
        <InfoBanner
          type={InfoBanner.Type.Info}
          styling={InfoBanner.Styling.Tight}
          body={translate('representative_step.info.title')}
        />
      )}
      <Section>
        <SeparatedList separator={<Divider />}>
          <InputText
            {...register(NewRepresentativeField.FirstNames, {
              required: true,
              validate: value => (value ? validateName(value) : undefined),
            })}
            styling={InputText.Styling.Wide}
            title={translate(isUpdatedNameInputs ? 'first_names.title_v2' : 'first_names.title')}
            placeholder={translate(
              isUpdatedNameInputs ? 'first_names.placeholder_v2' : 'first_names.placeholder',
            )}
            validation={
              renderValidation(validationMessage(NewRepresentativeField.FirstNames)) ||
              (isUpdatedNameInputs && translate('first_names.hint'))
            }
          />
          <InputText
            {...register(NewRepresentativeField.LastName, {
              required: true,
              validate: value => (value ? validateName(value) : undefined),
            })}
            styling={InputText.Styling.Wide}
            title={translate(isUpdatedNameInputs ? 'last_name.title_v2' : 'last_name.title')}
            placeholder={translate(
              isUpdatedNameInputs ? 'last_name.placeholder_v2' : 'last_name.placeholder',
            )}
            validation={
              renderValidation(validationMessage(NewRepresentativeField.LastName)) ||
              (isUpdatedNameInputs && translate('last_name.hint'))
            }
          />
          <Controller
            render={({ field }) => {
              const fieldPropsWithoutRef = omit(field, 'ref')

              return (
                <InputBirthdate
                  {...fieldPropsWithoutRef}
                  isPhone={!!breakpoints.phones}
                  styling={InputText.Styling.Wide}
                  name={NewRepresentativeField.Birthday}
                  title={translate('birthday.title')}
                  dayPlaceholder={inputBirthdateTranslate('birthdate.day')}
                  monthPlaceholder={inputBirthdateTranslate('birthdate.month')}
                  yearPlaceholder={inputBirthdateTranslate('birthdate.year')}
                  isoLocale={intl.locale}
                  validation={renderValidation(validationMessage(NewRepresentativeField.Birthday))}
                />
              )
            }}
            name={NewRepresentativeField.Birthday}
            control={control}
            rules={{
              required: true,
              validate: {
                invalid: value => !!value,
              },
            }}
          />
          <Controller
            control={control}
            name={NewRepresentativeField.Nationality}
            render={({ field: { onChange } }) => {
              return (
                <NationalitySelect
                  name={NewRepresentativeField.Nationality}
                  value={nationality}
                  showSuggestedCountries
                  title={translate('nationality.title')}
                  placeholder={translate('nationality.placeholder')}
                  validation={renderValidation(
                    validationMessage(NewRepresentativeField.Nationality),
                  )}
                  onChange={handleNationalityChange(onChange)}
                />
              )
            }}
          />
          <Controller
            control={control}
            name={NewRepresentativeField.Country}
            render={() => {
              return (
                <InputDropdown
                  isPhone={!!breakpoints.phones}
                  textSave={inputDropdownTranslate('actions.save')}
                  name={NewRepresentativeField.Country}
                  title={translate('country.title')}
                  placeholder={translate('country.placeholder')}
                  render={renderCountriesList}
                  value={countryInput?.title}
                  validation={renderValidation(validationMessage(NewRepresentativeField.Country))}
                />
              )
            }}
          />
          <InputText
            {...register(NewRepresentativeField.Email, {
              required: true,
            })}
            styling={InputText.Styling.Wide}
            title={translate('email.title')}
            placeholder={translate('email.placeholder')}
            validation={renderValidation(validationMessage(NewRepresentativeField.Email))}
          />
          <Cell styling={Cell.Styling.Wide}>
            <Controller
              name={NewRepresentativeField.IsSameLegalAddress}
              defaultValue={false}
              render={({ field: { value, name, onChange } }) => (
                <Checkbox
                  name={name}
                  text={translateBusinessAddress('legal_representative_address.check')}
                  checked={value}
                  onChange={event => {
                    onChange(event.target.checked)
                  }}
                />
              )}
              control={control}
            />
          </Cell>
          {isSameLegalAddress && !!businessAddress
            ? renderBusinessAddress(businessAddress)
            : renderRepresentativeAddressForm()}
        </SeparatedList>
      </Section>
      <Spacer size={Spacer.Size.Medium} />
    </SeparatedList>
  )
}

export default RepresentativeStep
